/**
 * NewThreadLanding.logic
 *
 * Pure helpers for the `/new` route. Kept separate from the React component
 * so the substantive decisions (which project to default to, what the heading
 * says, how to label intelligence tiers) are unit-testable without mounting
 * any UI.
 */
import type { ModelSelection, ProviderKind, ServerProvider } from "@cmmd-forge/contracts";
import { getProviderCatalogModel } from "@cmmd-forge/contracts";
import { belongsToDifferentProvider, normalizeModelSlug } from "@cmmd-forge/shared/model";
import {
  getDefaultServerModel,
  getProviderModels,
  resolveDefaultSelectableProvider,
  resolvePickableProviderSnapshots,
} from "../../providerModels";
import type {
  ForgeProviderAccessState,
  ProviderCredentialMode,
} from "../../cmmd/forgeProviderAccess";
import { sanitizeNewRefName } from "../BranchToolbar.logic";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import type { Project } from "../../types";

export {
  resolveNewThreadProviderInventory,
  resolveNewThreadProviderInventoryView,
} from "./NewThreadLanding.providerInventory";

export function resolveNewThreadModelSelection(input: {
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly preferredProvider: ProviderKind | null;
  readonly preferredModel: string | null;
  readonly pinPreferredSelection?: boolean;
  readonly providerAccess: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
}): ModelSelection | null {
  // The landing offers on the Pick verdict: a transient readiness flap must
  // not hide a provider the person could use seconds later, while durable
  // readiness facts still withhold it with their reason.
  const eligibleProviders = resolvePickableProviderSnapshots({
    providers: input.providers,
    access: input.providerAccess,
    credentialMode: input.credentialMode,
  });
  const preferredSnapshot = input.preferredProvider
    ? eligibleProviders.find((provider) => provider.provider === input.preferredProvider)
    : null;
  // Membership in the pick list IS the display verdict; re-checking snapshot
  // executability here would re-apply the transient flap this projection tolerates.
  const provider =
    preferredSnapshot?.provider ?? resolveDefaultSelectableProvider(eligibleProviders);
  const providerSnapshot = eligibleProviders.find((candidate) => candidate.provider === provider);
  if (!providerSnapshot) return null;

  const preferredPair =
    input.preferredProvider === provider && input.preferredModel !== null
      ? resolveExactProviderModelPair({
          providers: eligibleProviders,
          provider: providerSnapshot.provider,
          model: input.preferredModel,
        })
      : null;
  if (preferredPair?.status === "foreign") return null;
  if (input.pinPreferredSelection === true && preferredPair?.status !== "available") return null;
  return {
    provider: providerSnapshot.provider,
    model:
      preferredPair?.status === "available"
        ? preferredPair.model
        : getDefaultServerModel(eligibleProviders, providerSnapshot.provider),
  };
}

export type ExactProviderModelPairResolution =
  | { readonly status: "available"; readonly model: string }
  | { readonly status: "foreign" | "invalid" | "known-unavailable" };

/**
 * Classifies one exact provider/model pair against the runtime that will
 * execute it. Known built-ins must be present in that provider's live
 * inventory. Unknown slugs remain extensible (custom models and providers
 * without a compiled catalog), but a built-in owned by another provider is
 * always refused.
 */
export function resolveExactProviderModelPair(input: {
  readonly providers: ReadonlyArray<ServerProvider>;
  readonly provider: ProviderKind;
  readonly model: string | null | undefined;
}): ExactProviderModelPairResolution {
  const normalizedModel = normalizeModelSlug(input.model, input.provider);
  if (normalizedModel === null) return { status: "invalid" };
  if (belongsToDifferentProvider(normalizedModel, input.provider)) {
    return { status: "foreign" };
  }

  const publishedModel = getProviderModels(input.providers, input.provider).find(
    (model) => normalizeModelSlug(model.slug, input.provider) === normalizedModel,
  );
  if (publishedModel) {
    return { status: "available", model: normalizedModel };
  }

  const knownBuiltIn =
    input.provider === "codex" || input.provider === "claudeAgent"
      ? getProviderCatalogModel(input.provider, normalizedModel)
      : undefined;
  return knownBuiltIn
    ? { status: "known-unavailable" }
    : { status: "available", model: normalizedModel };
}

export interface ProjectSummary {
  readonly id: string;
  readonly environmentId: string;
  readonly name: string;
  /**
   * The runtime-local project id behind a stable catalog identity. Cloud rows
   * keep `id=cloud:<org>:<project>` across provision/reconnect, while this
   * field preserves the exact project record used for execution.
   */
  readonly resolvedProjectId?: string | null;
  /** Optional grayed-out suffix shown after the project name (e.g. "New project", "Personal Finances"). */
  readonly subLabel?: string | null;
  /**
   * Enough to draw the project the way every other list draws it. `icon`/
   * `iconUrl` are CMMD's own identity; `cwd` is what lets a plain local
   * checkout resolve its own favicon instead of settling for a letter.
   */
  readonly cwd?: string | null;
  readonly icon?: string | null;
  readonly iconUrl?: string | null;
}

export interface ExecutionSummary {
  readonly id: string;
  readonly label: string;
  readonly kind: "cloud" | "device";
}

export interface SavedExecutionEnvironmentSummary {
  readonly environmentId: string;
  readonly label: string;
}

export interface DesktopRuntimeExecutionSummary extends SavedExecutionEnvironmentSummary {
  readonly online: boolean;
  readonly inventorySynced: boolean;
}

/**
 * A cloud sprite the user can run on, named by the project that owns it.
 *
 * On Cloud a sprite is provisioned per CMMD project, so there is no runtime to
 * pick that is not somebody's project runtime. `provisioned` is false for a
 * registered-but-never-started project: it has no sprite yet, so it cannot host
 * a no-project chat without a provision the user did not ask for.
 */
export interface CloudRuntimeExecutionSummary {
  readonly environmentId: string;
  readonly label: string;
  readonly provisioned: boolean;
}

export interface DesktopRuntimeInventoryProjectSummary {
  readonly id: string;
  readonly environmentId: string;
  readonly name: string;
  readonly runtimeLabel: string;
}

/**
 * Can this surface host the runtime's scratch workspace?
 *
 * `workspace.ensureScratch` runs on the PRIMARY environment, so that
 * environment must be a real execution environment:
 *
 * - Desktop: the primary environment is the local Forge server. Correct target.
 * - Forge Cloud control surface: the primary environment is a workspace PROXY,
 *   not an execution plane. A scratch workspace created there lands on the
 *   Railway shell container rather than the user's sprite — the wrong machine,
 *   and one the runtime boundary keeps execution off.
 *
 * Cloud is withheld rather than pointed somewhere wrong. Targeting the selected
 * sprite is a separate design: it has to be connected first, and only sprites on
 * a commit that implements the RPC can answer it.
 */
export function canHostScratchWorkspace(input: {
  readonly primaryEnvironmentId: string | null;
  readonly primaryIsWorkspaceProxy: boolean;
  /**
   * The execution the user has selected. On Cloud this must be a CONCRETE
   * sprite environment; the aggregate `"cloud"` sentinel names no machine.
   */
  readonly selectedExecutionId?: string | null;
}): boolean {
  // A concrete cloud sprite is a real execution environment and can host the
  // bucket, whatever the primary environment happens to be. This is the Cloud
  // path: the user picked the sprite explicitly, so there is nothing to guess.
  if (isCloudRuntimeEnvironmentId(input.selectedExecutionId)) return true;
  // The aggregate sentinel is deliberately refused. It is the "some cloud
  // runtime" placeholder, and resolving it would mean picking a sprite on the
  // user's behalf — which can silently run their chat on another project's
  // machine.
  if (input.selectedExecutionId === CLOUD_EXECUTION_ID) return false;
  return input.primaryEnvironmentId !== null && !input.primaryIsWorkspaceProxy;
}

/**
 * Which environment should host the no-project bucket for this submission?
 *
 * Returns null when the answer is not knowable, which is a refusal rather than
 * a default: guessing here puts a user's chat on a machine they did not choose.
 */
export function resolveScratchTargetEnvironmentId(input: {
  readonly primaryEnvironmentId: string | null;
  readonly primaryIsWorkspaceProxy: boolean;
  readonly selectedExecutionId: string | null;
}): string | null {
  if (isCloudRuntimeEnvironmentId(input.selectedExecutionId)) return input.selectedExecutionId;
  if (input.selectedExecutionId === CLOUD_EXECUTION_ID) return null;
  if (input.primaryIsWorkspaceProxy) return null;
  return input.primaryEnvironmentId;
}

export function resolveNewThreadProviderCredentialMode(
  executionId: string,
): ProviderCredentialMode {
  return executionId === CLOUD_EXECUTION_ID ? "cmmd-cloud" : "runtime";
}

export function resolveNewThreadPickerProviderProps(input: {
  readonly inventoryKnown: boolean;
  readonly eligibleProviders: ReadonlyArray<ServerProvider>;
}): { readonly providers?: ReadonlyArray<ServerProvider> } {
  return input.inventoryKnown ? { providers: input.eligibleProviders } : {};
}

export const NO_PROJECT_SELECTION_KEY = "__forge:no-project__";
const CLOUD_EXECUTION_ID = "cloud";
// Imported rather than inlined so the two cloud environment namespaces stay
// defined in exactly one place (cloudProjectsGroup.logic).

export function projectSelectionKey(project: Pick<ProjectSummary, "environmentId" | "id">): string {
  return /^cloud:\d+:\d+$/.test(project.id) ? project.id : scopedKey(project);
}

/**
 * True for any cloud-owned environment id, provisioned or not. A
 * registered-but-unprovisioned project MUST pass this: otherwise it is filtered
 * out of the Cloud execution target and the user can never start the very first
 * thread that would provision it.
 */
function isCloudRuntimeEnvironmentId(environmentId: string | null | undefined): boolean {
  return environmentId ? classifyEnvironmentId(environmentId).isCloud : false;
}

export function resolveInitialExecutionId(input: {
  readonly activeEnvironmentId: string | null;
  readonly cloudCatalogRowCount: number;
  readonly isCloudControlSurface: boolean;
}): string {
  const shouldSelectCloud =
    input.isCloudControlSurface ||
    input.cloudCatalogRowCount > 0 ||
    isCloudRuntimeEnvironmentId(input.activeEnvironmentId);
  return shouldSelectCloud ? CLOUD_EXECUTION_ID : (input.activeEnvironmentId ?? "device");
}

export function filterProjectsForExecution(
  projects: readonly ProjectSummary[],
  executionId: string,
): ProjectSummary[] {
  return projects.filter((project) =>
    executionId === CLOUD_EXECUTION_ID
      ? isCloudRuntimeEnvironmentId(project.environmentId)
      : project.environmentId === executionId,
  );
}

export function resolveProjectForSelection<T extends Pick<ProjectSummary, "environmentId" | "id">>(
  projects: readonly T[],
  selectedProjectKey: string | null,
  defaultProject: ProjectSummary | null,
): T | null {
  const targetKey = resolveSelectedProjectKey({ selectedProjectKey, defaultProject });
  return targetKey === null
    ? null
    : (projects.find((project) => projectSelectionKey(project) === targetKey) ?? null);
}

export function resolveCloudSummaryForSelection(input: {
  readonly activeProjectPresent: boolean;
  readonly projectSummaries: readonly ProjectSummary[];
  readonly selectedProjectKey: string | null;
  readonly defaultProject: ProjectSummary | null;
}): ProjectSummary | null {
  if (input.activeProjectPresent) return null;
  return resolveProjectForSelection(
    input.projectSummaries,
    input.selectedProjectKey,
    input.defaultProject,
  );
}

/**
 * A checkout's own label. For a project imported from CMMD that is a slug of
 * the board title, which is exactly what the display-name join exists to
 * replace; this is the fallback when no linked record answers.
 */
export function deriveProjectDisplayName(project: Project): string {
  if (project.name?.trim()) return project.name;
  const pathName = project.cwd.split("/").findLast((segment) => segment.length > 0);
  return pathName ?? project.id;
}

export function resolvePreferredModel(
  provider: ProviderKind | null,
  selectedModelByProvider: Partial<Record<ProviderKind, string>>,
): string | null {
  return provider === null ? null : (selectedModelByProvider[provider] ?? null);
}

export function resolveWorktreeMode(defaultThreadEnvMode: string): WorktreeMode {
  return defaultThreadEnvMode === "local" ? "local" : "worktree";
}

export function buildExecutionSummaries(input: {
  readonly activeEnvironmentId: string | null;
  readonly cloudCatalogRowCount: number;
  readonly isCloudControlSurface: boolean;
  readonly savedEnvironments: readonly SavedExecutionEnvironmentSummary[];
  readonly desktopRuntimeInventory: readonly DesktopRuntimeExecutionSummary[];
  /**
   * The user's provisioned cloud sprites. Only consulted when
   * `expandCloudRuntimes` is set.
   */
  readonly cloudRuntimes?: readonly CloudRuntimeExecutionSummary[];
  /**
   * Replace the single aggregate "Cloud" entry with one entry per provisioned
   * sprite.
   *
   * Set when the user has chosen "Don't use a project". Normally the sprite is
   * implied by the project they picked, so one "Cloud" entry is enough. With no
   * project there is nothing to imply it, and the aggregate id is a sentinel
   * that names no machine — so submission would have to guess which sprite runs
   * the chat. Expanding the list makes the user say it instead.
   */
  readonly expandCloudRuntimes?: boolean;
}): ExecutionSummary[] {
  const activeDevice =
    !input.isCloudControlSurface &&
    input.activeEnvironmentId &&
    !isCloudRuntimeEnvironmentId(input.activeEnvironmentId)
      ? ({
          id: input.activeEnvironmentId,
          label: "This Device",
          kind: "device" as const,
        } satisfies ExecutionSummary)
      : null;
  const canonicalDesktopById = new Map(
    input.desktopRuntimeInventory.map((runtime) => [runtime.environmentId, runtime]),
  );
  const savedDevices = input.savedEnvironments
    .filter(
      (record) =>
        !isCloudRuntimeEnvironmentId(record.environmentId) &&
        record.environmentId !== input.activeEnvironmentId &&
        (canonicalDesktopById.get(record.environmentId)?.online ?? true) &&
        (canonicalDesktopById.get(record.environmentId)?.inventorySynced ?? true),
    )
    .map((record) => [record.environmentId, record] as const);
  const executableDesktopRuntimes = input.desktopRuntimeInventory
    .filter(
      (runtime) =>
        runtime.environmentId !== input.activeEnvironmentId &&
        runtime.online &&
        runtime.inventorySynced,
    )
    .map((runtime) => [runtime.environmentId, runtime] as const);
  const desktopDevices = [...new Map([...savedDevices, ...executableDesktopRuntimes]).values()]
    .toSorted((left, right) => left.label.localeCompare(right.label))
    .map((record) => ({
      id: record.environmentId,
      label: record.label,
      kind: "device" as const,
    }));
  const hasCloudRuntime =
    input.isCloudControlSurface ||
    input.cloudCatalogRowCount > 0 ||
    isCloudRuntimeEnvironmentId(input.activeEnvironmentId) ||
    input.savedEnvironments.some((record) => isCloudRuntimeEnvironmentId(record.environmentId));

  if (hasCloudRuntime) {
    const expandedCloudRuntimes =
      input.expandCloudRuntimes === true
        ? (input.cloudRuntimes ?? [])
            .filter((runtime) => runtime.provisioned)
            .toSorted((left, right) => left.label.localeCompare(right.label))
            .map(
              (runtime) =>
                ({
                  id: runtime.environmentId,
                  label: runtime.label,
                  kind: "cloud",
                }) satisfies ExecutionSummary,
            )
        : null;
    return [
      ...(expandedCloudRuntimes ?? [
        { id: CLOUD_EXECUTION_ID, label: "Cloud", kind: "cloud" } satisfies ExecutionSummary,
      ]),
      ...(activeDevice ? [activeDevice] : []),
      ...desktopDevices,
    ];
  }

  return [
    activeDevice ?? {
      id: "device",
      label: "Device",
      kind: "device",
    },
    ...desktopDevices,
  ];
}

/**
 * Resolve the project that should be selected when the user lands on /new.
 *
 * Preference order:
 *   1. `activeProjectKey` — the project the user is currently working in.
 *   2. The first id in `preferredOrder` that maps to a real project
 *      (the user's manual sidebar arrangement).
 *   3. Otherwise the first project in `projects` (server's natural order).
 *   4. `null` when there are no projects at all (empty state).
 *
 * ── why step 1 exists (CMMD-2266) ───────────────────────────────────────────
 *
 * `preferredOrder` is the persisted sidebar DRAG arrangement (`orderIndex` in
 * uiStateStore), and this docstring used to describe it as "most-recently-
 * touched". It never was: no writer records recency into it. So every entry
 * point that navigates to a bare `/new` — the sidebar header link, the command
 * palette — opened on whichever project happened to sit first in the sidebar,
 * regardless of where the user actually was.
 *
 * `activeProjectKey` comes from `uiStateStore.lastActiveProject`, which the
 * active route writes. Note what it is NOT sourced from: `threadLastVisitedAtById`
 * looks like a visit log but is an unread-clearing record — its writer only fires
 * for a thread with newly completed activity, so re-opening an already-read thread
 * records nothing, and `markThreadUnread` writes synthetic timestamps into it.
 * Deriving recency from it silently fails in the most common flow.
 *
 * That is not merely a cosmetic default. The resolved project decides
 * `activeProjectPresentation.environmentId` -> `activeCloudCatalogRow` ->
 * `useForgeProviderAccess({ runtimeId })`, so landing on the wrong project
 * silently repoints the model picker at a different runtime's provider
 * inventory and falls back to `"codex"` when that runtime publishes nothing.
 * Users saw it as "the provider changed itself to Claude/Codex".
 *
 * A stale or unknown `activeProjectKey` deliberately falls through to the old
 * behaviour rather than resolving to nothing: an empty selection is worse than
 * a wrong default.
 */
export function resolveDefaultProject(
  projects: readonly ProjectSummary[],
  preferredOrder: readonly string[],
  activeProjectKey?: string | null,
): ProjectSummary | null {
  if (projects.length === 0) return null;

  const projectByKey = new Map<string, ProjectSummary>();
  for (const project of projects) {
    projectByKey.set(scopedKey(project), project);
  }

  if (activeProjectKey) {
    const active = projectByKey.get(activeProjectKey);
    if (active) return active;
  }

  for (const key of preferredOrder) {
    const match = projectByKey.get(key);
    if (match) return match;
  }

  return projects[0] ?? null;
}

export function resolveConnectedProjectForSummary<
  T extends Pick<ProjectSummary, "environmentId" | "id">,
>(projects: readonly T[], summary: ProjectSummary | null): T | null {
  if (summary === null) return null;
  const projectId = summary.resolvedProjectId ?? summary.id;
  return (
    projects.find(
      (project) => project.environmentId === summary.environmentId && project.id === projectId,
    ) ?? null
  );
}

export function resolveSelectedProjectKey(input: {
  readonly selectedProjectKey: string | null;
  readonly defaultProject: ProjectSummary | null;
}): string | null {
  if (input.selectedProjectKey === NO_PROJECT_SELECTION_KEY) return null;
  if (input.selectedProjectKey) return input.selectedProjectKey;
  return input.defaultProject ? projectSelectionKey(input.defaultProject) : null;
}

/**
 * Build the centered heading copy. Codex pattern:
 *   "What should we build in <project>?"   when a project is active
 *   "What should we build?"                when no project is selected
 */
export function buildHeadingCopy(projectName: string | null): string {
  const segments = buildHeadingSegments(projectName);
  return `${segments.prefix}${segments.projectName ?? ""}${segments.suffix}`;
}

export interface HeadingSegments {
  readonly prefix: string;
  /** `null` when there is no name to hand a control. */
  readonly projectName: string | null;
  readonly suffix: string;
}

/**
 * The heading, split at the project name.
 *
 * t3code renders the name as a project *selector* rather than as text —
 * `What should we build in {projectSelector}?` — so the copy cannot arrive as
 * one baked string. Splitting it here keeps the wording in the logic module,
 * where the SSR guards already assert it, while letting the view decide that
 * the middle segment is a control.
 *
 * A blank name yields `null` for the middle segment, but the view still renders
 * the control there — the trigger carries its own "Choose a project" label. It
 * has to: the picker that used to sit in the strip below the composer is gone
 * (it duplicated this one, and t3code's own strip carries no project selector),
 * so this is the only picker on a desktop draft. Falling back to plain text
 * would strand a user with no project on the one page meant to give them one.
 * t3code's headline resolves the same way, reading `{selector} to start`.
 */
export function buildHeadingSegments(projectName: string | null): HeadingSegments {
  const trimmed = projectName?.trim() ?? "";
  if (trimmed.length === 0) {
    return { prefix: "", projectName: null, suffix: " to start" };
  }
  return { prefix: "What should we build in ", projectName: trimmed, suffix: "?" };
}

/**
 * Trigger label for the model picker when no provider inventory has resolved a
 * model yet, but one *will* be chosen on submit — a stopped cloud runtime or an
 * unconnected desktop runtime publishes no inventory until it connects. Without
 * this the picker falls back to a loading skeleton that never resolves on those
 * surfaces, which reads as a broken control while submit works fine.
 *
 * Returns `undefined` when the picker should keep its own default behaviour:
 * either a real model already resolved, or nothing is submittable so there is
 * no model to promise.
 */
export function resolveModelTriggerFallbackLabel(input: {
  readonly modelSelectionResolved: boolean;
  readonly modelResolvableOnSubmit: boolean;
}): string | undefined {
  if (input.modelSelectionResolved) return undefined;
  return input.modelResolvableOnSubmit ? "Default model" : undefined;
}

/**
 * Determine which landing variant to render. Two faces, one page:
 *   - "empty" — no projects yet; show the Add Project picker prominently.
 *   - "ready" — projects exist; show the composer-first layout.
 */
export function resolveLandingVariant(projects: readonly ProjectSummary[]): "empty" | "ready" {
  return projects.length === 0 ? "empty" : "ready";
}

/**
 * Filter a project list by a free-text search query. Case-insensitive,
 * matches against both `name` and `subLabel`.
 */
export function filterProjectsBySearch(
  projects: readonly ProjectSummary[],
  query: string,
): ProjectSummary[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [...projects];
  return projects.filter((project) => {
    if (project.name.toLowerCase().includes(q)) return true;
    if (project.subLabel?.toLowerCase().includes(q)) return true;
    return false;
  });
}

/**
 * Filter a branch list by a free-text search query. Case-insensitive.
 */
export function filterBranchesBySearch(branches: readonly string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return [...branches];
  // A ref cannot contain whitespace, so a query that does can only ever have
  // meant the dashed form — the name that branch would have been created with.
  // Without this, typing "new branch" hides an existing "new-branch", which is
  // the branch the user was plainly looking for. Only applied when sanitizing
  // actually changes the query, so single-word searches are untouched.
  const sanitized = sanitizeNewRefName(q);
  const matches = (branch: string) => {
    const lower = branch.toLowerCase();
    if (lower.includes(q)) return true;
    return sanitized.length > 0 && sanitized !== q && lower.includes(sanitized);
  };
  return branches.filter(matches);
}

/**
 * Intelligence tier — the human-facing label in the model selector matches
 * Codex's vocabulary (Low / Medium / High / Extra High) but maps to Forge
 * model selections under the hood.
 */
export type IntelligenceTier = "low" | "medium" | "high" | "extra-high";

export const INTELLIGENCE_TIER_LABELS: Record<IntelligenceTier, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  "extra-high": "Extra High",
};

export const INTELLIGENCE_TIERS_IN_ORDER: readonly IntelligenceTier[] = [
  "low",
  "medium",
  "high",
  "extra-high",
];

/**
 * The visible "model badge" at the right edge of the composer toolbar.
 * Codex shows "5.5 Low" — version + tier. For Forge we surface the active
 * model's marketing version (e.g. "4.7") + the selected intelligence tier.
 */
export function formatModelBadge(modelVersion: string, tier: IntelligenceTier): string {
  return `${modelVersion} ${INTELLIGENCE_TIER_LABELS[tier]}`;
}

/**
 * Worktree / "Start in" modes. Codex's set; Forge maps the first two
 * directly via DraftThreadEnvMode and treats the latter two as informational
 * pointers (web link / cloud variant).
 */
export type WorktreeMode = "local" | "worktree" | "web" | "cloud";

export const WORKTREE_MODE_LABELS: Record<WorktreeMode, string> = {
  local: "Project root",
  worktree: "New worktree",
  web: "Connect Forge web",
  cloud: "Send to cloud",
};

function scopedKey(project: Pick<ProjectSummary, "environmentId" | "id">): string {
  return `${project.environmentId}:${project.id}`;
}
